/**
 * React Query hooks for Customization Template admin API.
 *
 * All query keys include tenant to prevent cross-tenant cache collisions.
 * All queries disabled until tenant is available (auth hydration).
 *
 * Plan: 117-personalization-admin-ui, Epic 01
 */

import {
  useQuery,
  useMutation,
  useQueryClient,
  keepPreviousData,
} from "@tanstack/react-query";
import { useNavigate } from "react-router-dom";
import { useAuthStore } from "@/lib/store/authStore";
import { toast } from "sonner";
import { customizationAdminApi } from "./customizationAdminApi";
import type {
  TemplateListResponse,
  TemplateDetail,
  SaveTemplateInput,
  DeleteTemplateResponse,
  AddFieldInput,
  LinkedProductsResponse,
  BundleCustomizationConfig,
  VariantOption,
} from "./customizationAdminTypes";

function useTenant() {
  return useAuthStore((s) => s.currentTenant);
}

export const customizationQueryKeys = {
  all: (tenant: string | null) =>
    ["customization-templates", tenant] as const,
  list: (tenant: string | null, page: number, search: string, status: string) =>
    ["customization-templates", "list", tenant, page, search, status] as const,
  detail: (tenant: string | null, name: string) =>
    ["customization-templates", "detail", tenant, name] as const,
  linkedProducts: (tenant: string | null, templateName: string) =>
    ["customization-templates", "linked-products", tenant, templateName] as const,
  bundleConfig: (tenant: string | null, itemCode: string) =>
    ["customization-templates", "bundle-config", tenant, itemCode] as const,
};

// -- List --

export function useTemplateList(
  page = 0,
  pageLength = 20,
  search = "",
  status = "",
) {
  const tenant = useTenant();

  return useQuery<TemplateListResponse>({
    queryKey: customizationQueryKeys.list(tenant, page, search, status),
    queryFn: () => customizationAdminApi.list(search, status, page, pageLength),
    enabled: !!tenant,
    placeholderData: keepPreviousData,
  });
}

// -- Detail --

export function useTemplateDetail(name: string) {
  const tenant = useTenant();

  return useQuery<TemplateDetail>({
    queryKey: customizationQueryKeys.detail(tenant, name),
    queryFn: () => customizationAdminApi.detail(name),
    enabled: !!tenant && !!name,
  });
}

// -- Save (create or update) --

export function useSaveTemplate() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tenant = useTenant();

  return useMutation({
    mutationFn: (input: SaveTemplateInput) => customizationAdminApi.save(input),
    onSuccess: (data) => {
      toast.success("Template saved");
      queryClient.invalidateQueries({
        queryKey: customizationQueryKeys.all(tenant),
      });
      // Navigate to detail page if this was a create (no name in input)
      if (data.name) {
        navigate(`/inventory/customization-templates/${encodeURIComponent(data.name)}`, {
          replace: true,
        });
      }
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to save template");
    },
  });
}

// -- Delete --

export function useDeleteTemplate() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const tenant = useTenant();

  return useMutation({
    mutationFn: (name: string) => customizationAdminApi.delete(name),
    onSuccess: (data: DeleteTemplateResponse) => {
      const msg = data.linked_products_cleared > 0
        ? `Template deleted. ${data.linked_products_cleared} product(s) unlinked.`
        : "Template deleted";
      toast.success(msg);
      queryClient.invalidateQueries({
        queryKey: customizationQueryKeys.all(tenant),
      });
      navigate("/inventory/customization-templates", { replace: true });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to delete template");
    },
  });
}

// -- Add field (quick-add) --

export function useAddField() {
  const queryClient = useQueryClient();
  const tenant = useTenant();

  return useMutation({
    mutationFn: (input: AddFieldInput) => customizationAdminApi.addField(input),
    onSuccess: () => {
      toast.success("Field added");
      queryClient.invalidateQueries({
        queryKey: customizationQueryKeys.all(tenant),
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to add field");
    },
  });
}

// -- Reorder fields --

export function useReorderFields() {
  const queryClient = useQueryClient();
  const tenant = useTenant();

  return useMutation({
    mutationFn: ({
      templateName,
      fieldOrder,
    }: {
      templateName: string;
      fieldOrder: string[];
    }) => customizationAdminApi.reorderFields(templateName, fieldOrder),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customizationQueryKeys.all(tenant),
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to reorder fields");
    },
  });
}

// -- Linked products --

export function useLinkedProducts(templateName: string) {
  const tenant = useTenant();

  return useQuery<LinkedProductsResponse>({
    queryKey: customizationQueryKeys.linkedProducts(tenant, templateName),
    queryFn: () => customizationAdminApi.getLinkedProducts(templateName),
    enabled: !!tenant && !!templateName,
  });
}

// -- Bundle config (Epic 04) --

export function useBundleConfig(itemCode: string) {
  const tenant = useTenant();

  return useQuery<BundleCustomizationConfig>({
    queryKey: customizationQueryKeys.bundleConfig(tenant, itemCode),
    queryFn: () => customizationAdminApi.getBundleConfig(itemCode),
    enabled: !!tenant && !!itemCode,
  });
}

// -- Save shared fields (Epic 04) --

export function useSaveSharedFields() {
  const queryClient = useQueryClient();
  const tenant = useTenant();

  return useMutation({
    mutationFn: ({
      bundleItemRowName,
      sharedFields,
    }: {
      bundleItemRowName: string;
      sharedFields: string[];
    }) => customizationAdminApi.saveSharedFields(bundleItemRowName, sharedFields),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customizationQueryKeys.all(tenant),
      });
    },
    onError: (error: Error) => {
      toast.error(error.message || "Failed to save shared fields");
    },
  });
}

// -- Save linked attributes (Plan 198, Epic 04) --

export function useSaveLinkedAttributes() {
  const queryClient = useQueryClient();
  const tenant = useTenant();

  return useMutation({
    mutationFn: ({ bundleName, attrs }: { bundleName: string; attrs: string[] }) =>
      customizationAdminApi.saveLinkedAttributes(bundleName, attrs),
    onMutate: async ({ bundleName, attrs }) => {
      // Optimistic update — immediately reflect in UI
      const key = customizationQueryKeys.bundleConfig(tenant, bundleName);
      await queryClient.cancelQueries({ queryKey: key });
      const previous = queryClient.getQueryData<BundleCustomizationConfig>(key);
      if (previous) {
        queryClient.setQueryData<BundleCustomizationConfig>(key, {
          ...previous,
          linked_attributes: attrs,
        });
      }
      return { previous, key };
    },
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: customizationQueryKeys.all(tenant),
      });
      toast.success("Linked attributes updated");
    },
    onError: (error: Error, _vars, context) => {
      // Rollback optimistic update
      if (context?.previous && context?.key) {
        queryClient.setQueryData(context.key, context.previous);
      }
      toast.error(error.message || "Failed to save linked attributes");
    },
  });
}

// -- Variant options for template items (Plan 172, Epic 04) --

export function useVariantOptions(templateItemCode: string | null) {
  const tenant = useTenant();

  return useQuery<VariantOption[]>({
    queryKey: ["variant-options", tenant, templateItemCode],
    queryFn: () => customizationAdminApi.getVariantOptions(templateItemCode!),
    enabled: !!tenant && !!templateItemCode,
    staleTime: 60_000,
  });
}

// -- Validate for activation --

export function useValidateForActivation() {
  return useMutation({
    mutationFn: (name: string) =>
      customizationAdminApi.validateForActivation(name),
    onError: (error: Error) => {
      toast.error(error.message || "Failed to validate template");
    },
  });
}
